// Talking to Blip aloud (docs/BLIP_VR_EDITOR_DESIGN.md, "Speech"): the
// microphone, streamed to the server's GET /api/blip/listen (a WebSocket to
// ElevenLabs' speech to text, web/server/lib/elevenlabs.mjs), which answers
// with the words heard, each with its time from the start of the recording;
// and Blip's voice, POST /api/blip/say, a WAV of his words.
//
// The microphone runs only between start() and stop(). Everything it hears
// from start() on is sent, even before the server answers, so that words
// said as the talk button went down are not lost. While warm() keeps it
// open, as while a player is linked, the last half second before start()
// is sent too. stop() goes on listening for a moment, for the end of the
// last word.
//
// The samples are taken on the browser's audio thread (an audio worklet),
// not the page's, where a running program would delay them until some were
// lost, which spoils the words heard.

const RATE = 16000;
const KEEP = RATE / 2;            // samples kept before start(), while warm
const KEEP_STARTING = RATE * 10;  // samples kept while start() opens the microphone
const FRAME = 1024;               // samples in each frame sent
const OLD_FRAME = 4096;           // samples in each frame without worklets
const TAIL_MS = 300;              // how long stop() goes on listening

// The worklet: frames of FRAME samples, posted to the page.
const WORKLET = `registerProcessor("b3d-blip-microphone", class extends AudioWorkletProcessor {
  constructor() { super(); this.frame = new Float32Array(${FRAME}); this.used = 0; }
  process(inputs) {
    const input = inputs[0] && inputs[0][0];
    if (input) {
      for (let k = 0; k < input.length; ++k) {
        this.frame[this.used++] = input[k];
        if (this.used === this.frame.length) { this.port.postMessage(this.frame); this.frame = new Float32Array(${FRAME}); this.used = 0; }
      }
    }
    return true;
  }
});`;

// What stopped the microphone, for the user.
function microphoneProblem(error) {
  const name = error && error.name, message = (error && error.message) || "";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "Blip can't hear you: no microphone was found.";
  if (name === "NotReadableError") return "Blip can't hear you: the microphone is in use by another program, or Windows is blocking it.";
  if (name === "NotAllowedError" && /system/i.test(message)) {
    return "Blip can't hear you: the computer doesn't let apps use the microphone. On Windows, turn on Settings > Privacy & security > Microphone, and \"Let desktop apps access your microphone\".";
  }
  return "Blip can't hear you: the microphone is off or not allowed.";
}

// The browser's language, for the server to tell ElevenLabs.
const browserLanguage = () => (typeof navigator !== "undefined" && navigator.language) || "";

// 16-bit mono PCM frames as a WAV file's bytes.
export function framesToWav(frames, rate = RATE) {
  const samples = frames.reduce((sum, frame) => sum + frame.length, 0);
  const bytes = new Uint8Array(44 + samples * 2);
  const view = new DataView(bytes.buffer);
  const text = (at, value) => { for (let k = 0; k < value.length; ++k) bytes[at + k] = value.charCodeAt(k); };
  text(0, "RIFF"); view.setUint32(4, 36 + samples * 2, true); text(8, "WAVE");
  text(12, "fmt "); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, rate, true); view.setUint32(28, rate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  text(36, "data"); view.setUint32(40, samples * 2, true);
  let at = 44;
  for (const frame of frames) for (let k = 0; k < frame.length; ++k, at += 2) view.setInt16(at, frame[k], true);
  return bytes;
}

export class SpeechError extends Error {
  constructor(message, code = "") {
    super(message);
    this.code = code;
  }
}

// Whether the server can listen and speak.
export const speechAvailable = (cloud) => !!(cloud && cloud.config && cloud.config.speech);

export class Listener {
  constructor(cloud) {
    this.cloud = cloud;
    this.stream = null;
    this.context = null;
    this.processor = null;
    this.kept = [];               // Int16Array frames, the last half second
    this.socket = null;
    this.recording = null;        // { resolve, reject, partial, final }
    this.warmed = false;
    this.starting = false;        // while start() opens the microphone
    this.sent = [];               // the frames of the last recording, for lastWav()
  }

  // Opens the microphone, once.
  async open() {
    if (this.stream) return;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new SpeechError("This browser cannot use a microphone.", "microphone");
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true,
        noiseSuppression: true, autoGainControl: true } });
    } catch (error) {
      throw new SpeechError(microphoneProblem(error), "microphone");
    }
    this.context = new AudioContext({ sampleRate: RATE });
    const source = this.context.createMediaStreamSource(this.stream);
    if (this.context.audioWorklet) {
      const url = URL.createObjectURL(new Blob([WORKLET], { type: "text/javascript" }));
      try {
        await this.context.audioWorklet.addModule(url);
        this.processor = new AudioWorkletNode(this.context, "b3d-blip-microphone",
          { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1] });
        this.processor.port.onmessage = (event) => this.heard(event.data);
      } catch {
        this.processor = null;
      } finally {
        URL.revokeObjectURL(url);
      }
    }
    if (!this.processor) {
      // Without worklets, a script processor, on the page's thread.
      // Its frames are larger, as smaller ones are lost while the page is busy.
      this.processor = this.context.createScriptProcessor(OLD_FRAME, 1, 1);
      this.processor.onaudioprocess = (event) => this.heard(event.inputBuffer.getChannelData(0));
    }
    source.connect(this.processor);
    // Its output is silent, but a node is only run while it leads somewhere.
    this.processor.connect(this.context.destination);
  }

  // Keeps the microphone open between recordings, or lets it close.
  async warm(on) {
    this.warmed = on;
    if (on) await this.open();
    else if (!this.recording) this.close();
  }

  heard(samples) {
    const frame = new Int16Array(samples.length);
    for (let k = 0; k < samples.length; ++k) frame[k] = Math.max(-32768, Math.min(32767, Math.round(samples[k] * 32767)));
    if (this.recording && this.socket) {
      // Once the server is told to stop, it takes no more.
      if (this.recording.stopping && this.socket.readyState === WebSocket.OPEN) return;
      this.sent.push(frame);
      if (this.socket.readyState === WebSocket.OPEN) this.socket.send(frame.buffer);
      else this.recording.queued.push(frame);
      return;
    }
    this.kept.push(frame);
    const keep = this.starting ? KEEP_STARTING : KEEP;
    let total = this.kept.reduce((sum, part) => sum + part.length, 0);
    while (total - this.kept[0].length >= keep) total -= this.kept.shift().length;
  }

  // Starts a recording. onLive is called once the microphone is hearing,
  // and onPartial gets the words heard so far.
  async start(onPartial = () => {}, onLive = () => {}) {
    if (this.recording) throw new SpeechError("Blip is listening already.", "busy");
    // What the microphone hears while it opens belongs to this recording.
    if (!this.warmed) this.kept = [];
    this.starting = true;
    try {
      await this.open();
      if (this.context.state === "suspended") await this.context.resume();
    } finally {
      this.starting = false;
    }
    onLive();
    const url = new URL("blip/listen", this.cloud.base);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    const queued = this.kept.splice(0);
    this.sent = queued.slice();
    return new Promise((resolveStarted, rejectStarted) => {
      const socket = new WebSocket(url);
      socket.binaryType = "arraybuffer";
      this.socket = socket;
      const recording = { queued, partial: onPartial, resolve: null, reject: null, done: null, started: false,
        stopping: false };
      recording.done = new Promise((resolve, reject) => { recording.resolve = resolve; recording.reject = reject; });
      // A recording that is not stopped would wait for ever.
      recording.done.catch(() => {});
      this.recording = recording;
      socket.addEventListener("open", () => {
        socket.send(JSON.stringify({ type: "start", language: browserLanguage() }));
        for (const frame of recording.queued.splice(0)) socket.send(frame.buffer);
        recording.started = true;
        // Stopped before the server answered.
        if (recording.stopping) socket.send(JSON.stringify({ type: "stop" }));
        resolveStarted();
      });
      socket.addEventListener("message", (event) => {
        let message;
        try { message = JSON.parse(event.data); } catch { return; }
        if (message.type === "partial") recording.partial(message.text || "");
        else if (message.type === "final") this.finish({ text: (message.text || "").trim(), words: message.words || [] });
        else if (message.type === "error") this.fail(new SpeechError(message.message || "Blip couldn't hear that.", message.code || ""));
      });
      socket.addEventListener("close", () => {
        if (!recording.started) rejectStarted(new SpeechError("Blip can't listen just now.", "failed"));
        this.fail(new SpeechError("Blip stopped listening.", "closed"));
      });
      socket.addEventListener("error", () => {});
    });
  }

  // Stops the recording, and returns { text, words } once the last words are in.
  async stop() {
    const recording = this.recording;
    if (!recording) return { text: "", words: [] };
    // The end of the last word, said as the button was clicked.
    await new Promise((resolve) => setTimeout(resolve, TAIL_MS));
    if (this.recording !== recording) return recording.done;
    recording.stopping = true;
    if (this.socket && this.socket.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify({ type: "stop" }));
    const timer = setTimeout(() => this.fail(new SpeechError("Blip didn't hear back in time.", "timeout")), 15000);
    try {
      return await recording.done;
    } finally {
      clearTimeout(timer);
    }
  }

  finish(result) {
    const recording = this.recording;
    if (!recording) return;
    this.recording = null;
    const socket = this.socket;
    this.socket = null;
    if (socket) socket.close();
    recording.resolve(result);
    if (!this.warmed) this.close();
  }

  fail(error) {
    const recording = this.recording;
    if (!recording) return;
    this.recording = null;
    const socket = this.socket;
    this.socket = null;
    if (socket && socket.readyState === WebSocket.OPEN) socket.close();
    recording.reject(error);
    if (!this.warmed) this.close();
  }

  // The audio of the last recording, as it was sent, as a WAV file's bytes.
  lastWav() {
    return framesToWav(this.sent);
  }

  close() {
    if (this.processor) this.processor.disconnect();
    if (this.context) this.context.close().catch(() => {});
    if (this.stream) for (const track of this.stream.getTracks()) track.stop();
    this.stream = this.context = this.processor = null;
    this.kept = [];
  }
}

// Blip's voice for some words: a WAV's bytes.
export async function speak(cloud, text) {
  const response = await cloud.request("POST", "blip/say", { json: { text: text.slice(0, 500) }, raw: true });
  return new Uint8Array(await response.arrayBuffer());
}

// The first sentences of a reply, for speaking: up to two, without code or
// Markdown.
export function spokenPart(reply) {
  const text = reply.replace(/```[\s\S]*?```/g, " ").replace(/`([^`]*)`/g, "$1").replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/^\s*([-*•]|\d+[.)])\s+/gm, "").replace(/\s+/g, " ").trim();
  const sentences = text.match(/[^.!?]+[.!?]+(\s|$)/g) || (text ? [text] : []);
  return sentences.slice(0, 2).join("").trim().slice(0, 300);
}

// What the user pointed at as they spoke, for Blip's context: their words,
// each pointing word matched to what was pointed at as it was said, and every
// run of pointing. words: [{ text, start, end }] in seconds from the start of
// the recording; pointing: the player's message (play_link.bb,
// PlayLinkSendPointing).
const POINTING = new Set(["this", "that", "these", "those", "here", "there", "it", "them", "one"]);
const number = (value) => typeof value === "number" && Number.isFinite(value) ? String(+value.toFixed(2)) : "?";
const vector = (values) => Array.isArray(values) ? values.map(number).join(", ") : "";

function runText(run) {
  // Something the program made in code, not in the world: its name, from NameEntity.
  const made = run.made ? `"${run.made}", which the program makes in code (not part of the world)` : "nothing (empty space)";
  const target = run.object ? `${run.object}${run.name ? ` "${run.name}"` : ""}` : made;
  const looking = run.looking !== undefined && run.looking !== run.object ? `, looking at ${run.looking || "nothing"}` : "";
  return `${target} at ${vector(run.point)}${looking}`;
}

export function pointingContext(text, words, pointing) {
  const lines = [];
  const how = pointing ? `${pointing.player || "a player"}${pointing.vr ? ", in VR" : ""}` : "a player";
  lines.push(`The user said aloud, playing the world in ${how}: "${text}"`);
  const runs = pointing && Array.isArray(pointing.runs) ? pointing.runs : [];
  if (!runs.length) {
    lines.push("Nothing was pointed at as they spoke.");
  } else {
    const at = (time) => runs.find((run) => time >= run.from && time <= run.to) ||
      runs.reduce((best, run) => Math.abs(run.from - time) < Math.abs(best.from - time) ? run : best, runs[0]);
    const matched = [];
    for (let index = 0; index < (words || []).length; ++index) {
      const word = words[index];
      const plain = String(word.text || "").toLowerCase().replace(/[^a-z']/g, "");
      if (!POINTING.has(plain)) continue;
      // "that table": this, that, these and those take the word after them.
      const following = words.slice(index + 1).find((other) => /[a-z]/i.test(other.text || ""));
      const phrase = /^(this|that|these|those)$/.test(plain) && following && !POINTING.has(following.text.toLowerCase()) ?
        `${word.text} ${following.text}` : word.text;
      matched.push(`- "${phrase}" (${number(word.start)} s): pointing at ${runText(at(word.start))}`);
    }
    if (matched.length) {
      lines.push("While speaking:");
      lines.push(...matched);
    }
    lines.push(`Pointing, all of it (by ${pointing.by || "pointer"}):`);
    for (const run of runs.slice(0, 20)) lines.push(`- ${number(run.from)}-${number(run.to)} s: ${runText(run)}`);
  }
  const selected = pointing && Array.isArray(pointing.selected) ? pointing.selected : [];
  lines.push(`Selected: ${selected.join(", ") || "nothing"}.${pointing && pointing.position ? ` The user is at ` +
    `${vector(pointing.position)}, facing yaw ${number(pointing.yaw)}.` : ""}`);
  return lines.join("\n");
}
